import React from 'react';
import { PayoutTransaction, SellerOrder, TransactionType } from '@shopify-multiseller/graphql/partner/types';
import { Link } from 'react-router-dom';
import { SectionCard } from '../../components/SectionCard';
import { formatCents, formatDate } from '../../helpers/formatters';
import { useShopCurrency } from '../../hooks';
import { PayoutStatusBadge } from '../../components/StatusBadge';
import { isPayoutPaid } from './payoutState';

const manualTypeName = (transactionType: TransactionType): string | undefined => {
    switch (transactionType) {
        case TransactionType.Bank:
        case TransactionType.BankManual:
            return 'Bank transfer';
        case TransactionType.Cash:
            return 'Cash';
        case TransactionType.Paypal:
            return 'PayPal';
        default:
            return undefined;
    }
};

/**
 * How the money was sent. Returns nothing when the shape doesn't say — the
 * line read "Dec 2, 2020 · Unknown", which tells a seller their money went
 * somewhere unidentified. Saying only the date is the honest version.
 */
const transferLabel = (transaction: PayoutTransaction): string | undefined => {
    switch (transaction.__typename) {
        case 'PayoutTransactionStripe':
            return 'Bank transfer (Stripe)';
        case 'PayoutTransactionPaypal':
            return 'PayPal';
        case 'PayoutTransactionManual':
            return manualTypeName(transaction.to.type as unknown as TransactionType);
        default:
            return undefined;
    }
};

type PayoutSummaryProps = {
    order: SellerOrder;
};

/**
 * The payout this order landed in: how much, when, and how it was sent.
 *
 * Previously three stacked card sections with icon-only labels — a calendar
 * glyph next to a date and a card glyph next to a payment method — which
 * needed screen-reader-only text to say what they were. The facts are short
 * enough to just name.
 */
const PayoutSummary: React.FC<PayoutSummaryProps> = ({ order }) => {
    const shopCurrency = useShopCurrency();

    if (!order.payout) return null;

    const isPaid = isPayoutPaid(order);

    const whenLabel = isPaid
        ? order.payout.payoutDate
        : (order.payout.payoutDueDate ?? order.payout.payoutDate);

    const transaction = order.payout.transaction;
    const manualTransaction = transaction.__typename === 'PayoutTransactionManual' ? transaction : null;
    // Prefer the actual transaction currency (fetched by the payout
    // fragment); fall back to the shop currency for anything else.
    const currencyCode =
        transaction.__typename === 'PayoutTransactionStripe' || transaction.__typename === 'PayoutTransactionManual'
            ? transaction.to.currencyCode
            : shopCurrency;

    return (
        <SectionCard
            title={`Payout #${order.payout.idAlias}`}
            testId="order-payout-summary"
            quiet
            trailing={<PayoutStatusBadge hasPayout={isPaid} />}
        >
            <p className="text-2xl font-semibold tabular-nums text-foreground">
                {formatCents({
                    amount: manualTransaction?.to?.amount ?? order.totalPayout,
                    currencyCode,
                })}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
                {[
                    // A payout still to come is dated by when it is due, and says
                    // so. Printing its date bare read as the day it went out.
                    whenLabel &&
                        `${isPaid ? '' : 'Due '}${formatDate(whenLabel, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                        })}`,
                    transferLabel(transaction),
                ]
                    .filter(Boolean)
                    .join(' · ')}
            </p>
            <Link
                to={`/payouts/${order.payout.id}`}
                className="mt-3 inline-block text-sm text-primary hover:underline"
            >
                {'View payout →'}
            </Link>
        </SectionCard>
    );
};

export default PayoutSummary;
